import moment from 'moment';
import { Grid } from '@mui/material';
import { useState } from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DateInput,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  FormTab,
  FunctionField,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  SearchInput,
  SelectArrayInput,
  SelectInput,
  Show,
  Tab,
  TabbedForm,
  TabbedShowLayout,
  TextField,
  TextInput,
  maxValue,
  minValue,
  required,
  useGetIdentity,
  useRecordContext
} from 'react-admin';
import { useParams } from 'react-router-dom';
import { List, Tooltip, VehicleAverageChart, VehicleOperationInput } from '../components';
import Permission from '../components/Permission';
import { PERMISSIONS } from '../constants';
import { UserRole } from '../providers/authProvider';
import { getCompanyId } from '../utils/utils';
import { DriverVehiclesDatagrid } from './Driver';
import { FillinDatagrid } from './Fillin';
import { LicensePlateInput } from '../utils/Inputs';
import MustInformOdometerBooleanInput, { MustInformOdometerOrigin } from '../components/MustInformOdometerBooleanInput';
import { FillinLimitType } from '../models';

const FormEnum = {
  edit: 'edit',
  create: 'create',
}

const VehicleTitle = () => {
  const record = useRecordContext();
  if (!record) { return <></>; };
  return <span>{`${record.name} - ${record.licensePlate}`}</span>;
};

const OperationTextField = (props) => (
  <FunctionField {...props} render={record => record ? `${('0000' + record.code).slice(-4)} - ${record.name}` : ''} />
);

export const VehicleDatagrid = props => {
  return (
    <Datagrid
      {...props}
      bulkActionButtons={false}
      expand={<DriverVehiclesDatagrid bulkActionButtons={false} />}
    >
      <TextField source="name" label="Nome" />
      <TextField source="licensePlate" label="Placa" />
      <TextField source="brand" label="Marca" />
      <TextField source="model" label="Modelo" />
      <TextField source="year" label="Ano" />
      <NumberField source="odometer" label="Odômetro" locales="pt-BR" />
      <BooleanField source="mustInformOdometer" label="Obrigar a informar odômetro" />
      <NumberField source="consumptionAverageTopLimit" label="Média máxima de consumo (km/L)" locales="pt-BR" emptyText='Sem limite' />
      <NumberField source="consumptionAverageBottomLimit" label="Média mínima de consumo (km/L)" locales="pt-BR" emptyText='Sem limite' />
    </Datagrid>
  );
};

const VehicleShow = props => {
  const { id } = useParams();
  return (
    <Show title={<VehicleTitle />} {...props}>
      <TabbedShowLayout>
        <Tab label="Abastecimentos">
          <ReferenceManyField
            reference="fillins"
            target="vehicleId"
            addLabel={false}
            sort={{ field: "createdAt", order: "DESC" }}
            pagination={<Pagination />}
          >
            <FillinDatagrid />
          </ReferenceManyField>
        </Tab>
        <Tab label="Resumo">
          <TextField source="id" label="ID" />
          <TextField source="name" label="Nome" />
          <ReferenceField label="Transportadora" source="subCompanyId" reference="sub-companies" emptyText="Não definido">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField label="Gestor" source="managerId" reference="managers" emptyText="Não definido">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="vehicleGroupId" reference="vehicle-groups" label="Grupo" emptyText="Não definido">
            <TextField source="name" />
          </ReferenceField>
          <ReferenceField source="operationId" reference="operations" label="Operação" emptyText="Não definido">
            <OperationTextField />
          </ReferenceField>
          <TextField source="licensePlate" label="Placa" />
          <TextField source="brand" label="Marca" />
          <TextField source="model" label="Modelo" />
          <TextField source="year" label="Ano" />
          <TextField source="brand" label="Família" />
          <NumberField source="tankCapacity" label="Capacidade do tanque (L)" locales="pt-BR" />
          <NumberField source="arlaCapacity" label="Capacidade de arla" locales="pt-BR" />
          <NumberField source="odometer" label="Odômetro" locales="pt-BR" />
          <BooleanField source="mustInformOdometer" label="Obrigar a informar odômetro" />
          <FunctionField 
            label="Tipo de limite" 
            render={record => record.fillinLimitType ? FillinLimitType.translateType(record.fillinLimitType) : 'Nenhum'} 
          />
          <NumberField source="fillinLimitValue" label="Limite em litros" locales="pt-BR" emptyText="Sem limite" />
          <NumberField source="consumptionAverageTopLimit" label="Média máxima de consumo (km/L)" locales="pt-BR" />
          <NumberField source="consumptionAverageBottomLimit" label="Média mínima de consumo (km/L)" locales="pt-BR" />
          <NumberField source="expectedAverage" label="Média de consumo esperada (Km/L)" locales="pt-BR" />
        </Tab>
        <Tab label="Média">
          <div>
            <List
              resource="vehicles/averages"
              title="Média"
              filters={[
                <DateInput source="from" label="De" options={{ format: 'DD/MM/YYYY', disableFuture: true }} alwaysOn />,
                <DateInput source="to" label="Até" options={{ format: 'DD/MM/YYYY', disableFuture: true }} alwaysOn />,
              ]}
              filter={{
                vehicleId: id,
              }}
              filterDefaultValues={{
                from: moment().startOf('day').subtract(1, 'month').toISOString(),
                to: moment().endOf('day').toISOString(),
              }}
              pagination={false}
              exporter={false}
            >
              <VehicleAverageChart styleContent={{ paddingTop: 20 }} />
            </List>
          </div>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

const FilterVehicle = (props) => {
  return (
    <Filter {...props}>
      <SearchInput source="search" alwaysOn />
      <SelectInput
        label="Status"
        source="isInactive"
        emptyText="Todos"
        choices={[{ id: true, name: "Inativos" }, { id: false, name: "Ativos" }]}
        alwaysOn
      />
    </Filter>
  )
}

const VehicleList = props => {
  const { identity } = useGetIdentity();
  return (
    <List
      {...props}
      title="Veículos"
      filters={<FilterVehicle />}
      filter={getCompanyId() ? { companyId: getCompanyId() } : null}
      filterDefaultValues={{ isInactive: false }}
    >
      <Datagrid rowClick="show"
        bulkActionButtons={identity && [UserRole.admin, UserRole.company].includes(identity.role)}>
        <TextField source="name" label="Nome" />
        <TextField source="licensePlate" label="Placa" />
        <NumberField source="odometer" label="Odômetro" locales="pt-BR" />
        <Permission userRole={UserRole.admin}>
          <ReferenceField source="companyId" label="Grupo de Transportadora" reference="companies" allowEmpty>
            <TextField source="name" />
          </ReferenceField>
        </Permission>
        <ReferenceField source="subCompanyId" label="Transportadora" reference="sub-companies">
          <TextField source="name" />
        </ReferenceField>
        <BooleanField source="mustInformOdometer" label="Obrigar a informar odômetro" />
        {identity && [UserRole.admin, UserRole.company].includes(identity.role) && <EditButton />}
      </Datagrid>
    </List>
  );
};

const FormVehicle = ({ formType }) => {
  const { identity } = useGetIdentity();
  const [useTankCapacity, setUseTankCapacity] = useState(true);
  const companyId = getCompanyId();

  return (
    <TabbedForm defaultValues={{ companyId }}>
      <FormTab label="Geral">
        <Grid container>
          {
            formType === FormEnum.edit &&
            <Grid container xs={12} md={10} xl={8} spacing={2}>
              <Grid item xs={12} md={3}>
                <TextInput disabled label="ID" source="id" />
              </Grid>
            </Grid>
          }
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Permission userRole={UserRole.admin}>
              <Grid item xs={12} md={6}>
                <ReferenceInput source="companyId" reference="companies" sort={{ field: 'name', order: 'ASC' }} pagination={null} perPage={null}>
                  <SelectInput label="Grupo de Transportadora" optionText="name" fullWidth isRequired />
                </ReferenceInput>
              </Grid>
            </Permission>
            <Grid item xs={12} md={6}>
              <ReferenceInput
                label="Transportadora"
                source="subCompanyId"
                reference="sub-companies"
                filter={{ companyId: localStorage.getItem('companyId') }}
                sort={{ field: "name", order: "ASC" }}
                validate={required()}>
                <SelectInput label="Transportadora" optionText="name" isRequired fullWidth />
              </ReferenceInput>
            </Grid>
          </Grid>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <FormDataConsumer>
                {({ formData }) => {
                  return (
                    <ReferenceInput
                      source="costCenterId"
                      reference="cost-centers"
                      sort={{ field: 'name', order: 'ASC' }}
                      pagination={null}
                      perPage={null}
                      filter={
                        identity?.role === UserRole.company ? { companyId: getCompanyId() }
                          : formData.companyId ? { companyId: formData.companyId } : null
                      }
                    >
                      <SelectInput label="Centro de Custo" optionText="name" emptyText="ra.action.clear_input_value" fullWidth />
                    </ReferenceInput>
                  )
                }}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormDataConsumer>
                {({ formData }) => {
                  return (
                    <ReferenceInput
                      source="managerId"
                      reference="managers"
                      sort={{ field: 'name', order: 'ASC' }}
                      filter={
                        identity?.role === UserRole.company ? { companyId }
                          : formData.companyId ? { companyId: formData.companyId } : null
                      }
                      pagination={null}
                      perPage={null}
                      allowEmpty
                    >
                      <SelectInput label="Gestor" optionText="name" emptyText="ra.action.clear_input_value" fullWidth />
                    </ReferenceInput>
                  )
                }}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={12} md={4}>
              <VehicleOperationInput source="operationId" reference="operations" label="Operação" />
            </Grid>
          </Grid>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <TextInput source="name" label="Nome" fullWidth />
            </Grid>
            <Grid item sx={12} md={4}>
              <FormDataConsumer>
                {({ formData, ...rest }) => formData.companyId && (
                  <ReferenceInput
                    {...rest}
                    source="vehicleGroupId"
                    reference="vehicle-groups"
                    filter={{ companyId: formData.companyId }}
                    allowEmpty
                  >
                    <SelectInput label="Grupo de Veículos" optionText="name" emptyText="ra.action.clear_input_value" fullWidth />
                  </ReferenceInput>
                )}
              </FormDataConsumer>
            </Grid>
            <Grid item xs={12} md={4}>
              <LicensePlateInput source="licensePlate" label="Placa" isRequired fullWidth />
            </Grid>
          </Grid>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item sx={12} md={3}>
              <TextInput source="brand" label="Marca" fullWidth />
            </Grid>
            <Grid item sx={12} md={3}>
              <TextInput source="model" label="Modelo" fullWidth />
            </Grid>
            <Grid item sx={12} md={3}>
              <TextInput source="family" label="Família" fullWidth />
            </Grid>
            <Grid item sx={12} md={3}>
              <TextInput source="year" label="Ano" fullWidth />
            </Grid>
          </Grid>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <FormDataConsumer>
              {({ formData }) => {
                !useTankCapacity && (formData.tankCapacity = null);
                return (useTankCapacity &&
                  <Grid item sx={12} md={4}>
                    <NumberInput
                      source="tankCapacity"
                      label="Capacidade do tanque (L)"
                      locales="pt-BR"
                      fullWidth
                      validate={minValue(1)}
                    />
                  </Grid>
                );
              }}
            </FormDataConsumer>
            <Grid item xs={12} md={4}>
              <NumberInput source="arlaCapacity" label="Capacidade de arla" locales="pt-BR" fullWidth validate={minValue(1)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Tooltip sxContainer={{ alignItems: 'flex-start' }} placement="top-start" title='O motorista será capaz de abastecer apenas com os combustíveis selecionados.'>
                <ReferenceArrayInput
                  helperText="O motorista será capaz de abastecer apenas com os combustíveis selecionados."
                  sort={{ field: 'name', order: 'ASC' }}
                  perPage={1000}
                  source="fuelIds"
                  reference="fuels"
                >
                  <FormDataConsumer>
                    {(fuelsProps) => {
                      const { formData: { fuelIds }, choices } = fuelsProps;
                      if (fuelIds && choices && choices.length) {
                        const hasLiquidFuel = fuelIds.some((fuelId) =>
                          choices.find(({ id }) => fuelId === id).acronym !== 'GNV');
                        setUseTankCapacity(hasLiquidFuel);
                      }

                      return <SelectArrayInput {...fuelsProps} optionText="name" label="Combustíveis" fullWidth />;
                    }}
                  </FormDataConsumer>
                </ReferenceArrayInput>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <NumberInput source="odometer" label="Odômetro" locales="pt-BR" fullWidth defaultValue={0} />
            </Grid>
            <Grid item xs={12} md={3} sx={{ marginBlock: 'auto' }}>
              <MustInformOdometerBooleanInput source="mustInformOdometer" label="Obrigar a informar odômetro" origin={MustInformOdometerOrigin.Vehicle} />
            </Grid>
          </Grid>
          <Grid container xs={12} md={10} xl={8} spacing={2}>
            <Grid item xs={12} md={4}>
              <SelectInput
                label="Tipo de limite"
                source="fillinLimitType"
                choices={FillinLimitType.fillinLimitTypeChoices}
                emptyText="Nenhum"
                emptyValue={undefined}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <NumberInput
                source="fillinLimitValue"
                label="Limite em litros"
                step={5}
                validate={minValue(0)}
                fullWidth
              />
            </Grid>
          </Grid>
          {
            formType === FormEnum.edit &&
            <Grid container xs={12} md={10} xl={8} spacing={2}>
              <Grid item sx={12} md={4}>
                <BooleanInput source="isInactive" label="Inativar veículo" />
              </Grid>
            </Grid>
          }
        </Grid>
      </FormTab>
      <FormTab label="Desempenho">
        <NumberInput
          source="consumptionAverageTopLimit"
          label="Média máxima de consumo (km/L)"
          locales="pt-BR"
          validate={[minValue(1), required()]}
        />
        <FormDataConsumer>
          {({ formData }) => (
            <NumberInput
              source="consumptionAverageBottomLimit"
              label="Média mínima de consumo (km/L)"
              locales="pt-BR"
              validate={[maxValue(formData.consumptionAverageTopLimit, "O valor deve ser menor ou igual a Média máxima de consumo"), required()]}
            />
          )}
        </FormDataConsumer>
        <NumberInput
          source="expectedAverage"
          label="Média de consumo esperada (km/L)"
          locales="pt-BR"
          validate={formType === FormEnum.edit ? [minValue(1), required()] : undefined}
        />
      </FormTab>
    </TabbedForm>
  );
};

const VehicleEdit = props => {
  return (
    <Edit title='Editar veículo' {...props}>
      <FormVehicle formType={FormEnum.edit} />
    </Edit>
  );
};

const VehicleCreate = props => {
  return (
    <Create title='Adicionar veículo' {...props}>
      <FormVehicle formType={FormEnum.create} />
    </Create >
  );
};

const resource = {
  list: VehicleList,
  edit: VehicleEdit,
  create: VehicleCreate,
  show: VehicleShow,
  name: 'vehicles',
  permissions: [PERMISSIONS.LIST_VEHICLES]
}
export default resource;